import { RefObject } from 'react';
import { BehaviorSubject } from 'rxjs';
import { GridApiPro } from '@mui/x-data-grid-pro';
import { GridSortDirection } from '@mui/x-data-grid-pro';
import { getCommissionsInfo } from '../Api/CommissionsInfo';

export interface OrderRequest {
  sort: string;
  direction: GridSortDirection;
}

export interface ApiFilter {
  page?: number;
  pageSize?: number;
  orders?: OrderRequest[];
  [key: string]: string | number | boolean | undefined | OrderRequest[];
}

export interface ApiAdvancedFilter {
  page?: number;
  pageSize?: number;
  logicalOperator?: string;
  filters?: FilterableField[];
  orders?: OrderRequest[];
}

export interface FilterableField {
  field: string;
  operator: string;
  value: string | number | string[] | number[];
}

export interface PaginatedResponse<T> {
  data: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export type UserInfo = { email: string; id: string };

export type CommissionsUserInfo = {
  cea: UserInfo[];
  csm: UserInfo[];
};

export type CommissionsAssetInfo = {
  serviceStatus: string[];
  commissionsStatus: string[];
};

export type CommissionsCustomerInfo = {
  customerSegment: string[];
  partnershipSegmentation: string[];
};

export type CommissionsAccountInfo = {
  salesAgents: string[];
  suppliers: string[];
};

export type CommissionsInfo = {
  user: CommissionsUserInfo;
  asset: CommissionsAssetInfo;
  customer: CommissionsCustomerInfo;
  account: CommissionsAccountInfo;
};

export interface GeneralState {
  gridRefs: { [page: string]: RefObject<GridApiPro | null> };
  selectedTab: {
    [key: string]: number;
  };
  commissionsInfo: CommissionsInfo;
}

export const defaultState: GeneralState = {
  gridRefs: {},
  selectedTab: {
    customers: 0,
    assets: 0,
    provisionalAssets: 0,
    admin: 0,
    data: 0,
    links: 0,
    rpmAccounts: 0,
    tickets: 0,
    tfus: 0,
    payments: 0,
    corrections: 0,
    adjustments: 0,
    attributions: 0,
    history: 0
  },
  commissionsInfo: {
    user: { cea: [], csm: [] },
    asset: {
      serviceStatus: [],
      commissionsStatus: []
    },
    customer: {
      customerSegment: [],
      partnershipSegmentation: []
    },
    account: {
      salesAgents: [],
      suppliers: []
    }
  }
};

const getDefaultState = () => {
  const localState = localStorage.getItem('selectedTab') || '';
  if (localState) {
    return {
      ...defaultState,
      ...JSON.parse(localState)
    };
  }
  return defaultState;
};

const subject = new BehaviorSubject<GeneralState>(getDefaultState());
export const getState = () => subject.getValue();

export const getSelectedTab = () => {
  const localState = localStorage.getItem('selectedTab') || '';
  const subjectValue = subject.getValue().selectedTab;
  if (localState) {
    return { ...subjectValue, ...JSON.parse(localState) };
  }
  return subjectValue;
};

export const assetCommissionsStatusOptions: string[] = [
  'Commissioning',
  'Partially Commissioning',
  'Pending Commissions',
  'Decommissioned',
  'Non-Commissionable',
  'Cancelled',
  'Legacy Archive'
];

const sortCommissionsStatus = (statuses: string[]): string[] => {
  const orderMap = assetCommissionsStatusOptions.reduce(
    (acc, status, index) => {
      acc[status] = index;
      return acc;
    },
    {} as Record<string, number>
  );
  return [...statuses].sort((a, b) => {
    const indexA = orderMap[a] !== undefined ? orderMap[a] : 99;
    const indexB = orderMap[b] !== undefined ? orderMap[b] : 99;
    return indexA - indexB;
  });
};

let commissionsInfoLoaded = false;
let commissionsInfoRequest: Promise<void> | null = null;
let commissionsInfoGeneration = 0;

// A request in flight when the session is cleared cannot be cancelled, so it is dropped by generation
// instead -- otherwise its result would repopulate the store the clear had just emptied.
const loadCommissionsInfo = async (generation: number): Promise<void> => {
  const { data } = await getCommissionsInfo();
  if (generation !== commissionsInfoGeneration) return;

  const currentState = { ...subject.getValue() };
  if (data) {
    if (data.asset && data.asset.commissionsStatus) {
      data.asset.commissionsStatus = sortCommissionsStatus(data.asset.commissionsStatus);
    }
    currentState.commissionsInfo = data;
    commissionsInfoLoaded = true;
  }

  subject.next(currentState);
};

// Session-static filter lists: every caller means "ensure loaded" -- one success is final for the page
// load, a load that returned nothing may retry, and an explicit refresh still goes to the server.
export const fetchCommissionsInfo = async (forceRefresh = false): Promise<void> => {
  if (commissionsInfoLoaded && !forceRefresh) return;
  if (commissionsInfoRequest) return commissionsInfoRequest;

  const request: Promise<void> = loadCommissionsInfo(commissionsInfoGeneration).finally(() => {
    if (commissionsInfoRequest === request) commissionsInfoRequest = null;
  });
  commissionsInfoRequest = request;

  return request;
};

export const getCommissionsInfoByType = (
  objectType: keyof CommissionsInfo,
  field:
    | keyof CommissionsUserInfo
    | keyof CommissionsAssetInfo
    | keyof CommissionsCustomerInfo
    | keyof CommissionsAccountInfo
): UserInfo[] | string[] => {
  const commissionsInfo = subject.getValue().commissionsInfo;
  const val = (commissionsInfo[objectType] as Record<string, UserInfo[] | string[]>)[field];
  return val;
};

export const getGeneralStoreSubject = (): BehaviorSubject<GeneralState> => subject;

export const updateState = (update: Partial<GeneralState>) => {
  const currentState = { ...subject.getValue() };
  subject.next({ ...currentState, ...update });

  if ('selectedTab' in update) {
    localStorage.setItem('selectedTab', JSON.stringify(update?.selectedTab));
  }
};

export const updateGridRef = (page: string, ref: RefObject<GridApiPro | null>) => {
  const currentStateUpdate = { ...subject.getValue() };

  currentStateUpdate.gridRefs = {
    ...currentStateUpdate.gridRefs,
    [page]: ref
  };

  subject.next(currentStateUpdate);
};

export const clear = () => {
  commissionsInfoLoaded = false;
  commissionsInfoRequest = null;
  commissionsInfoGeneration += 1;
  subject.next(defaultState);
};

export const blobToExcel = (data: Blob, fileName: string) => {
  const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
